import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  GripVertical,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    category: 'custom',
    duration_minutes: 30,
    lessons: [
      {
        id: 'lesson_1',
        title: '',
        type: 'text',
        content: '',
        duration_minutes: 10
      }
    ],
    quiz_questions: [
      {
        id: 'q_1',
        question: '',
        type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: 0
      }
    ]
  });

  const categories = [
    { id: 'osha', name: 'OSHA' },
    { id: 'safety', name: 'Safety' },
    { id: 'compliance', name: 'Compliance' },
    { id: 'custom', name: 'Custom' }
  ];

  const lessonTypes = [
    { id: 'text', name: 'Text/Reading', icon: FileText },
    { id: 'video', name: 'Video', icon: Video },
    { id: 'interactive', name: 'Interactive', icon: BookOpen }
  ];

  const handleCourseChange = (field, value) => {
    setCourseData(prev => ({ ...prev, [field]: value }));
  };

  // Lesson handlers
  const addLesson = () => {
    setCourseData(prev => ({
      ...prev,
      lessons: [
        ...prev.lessons,
        {
          id: `lesson_${Date.now()}`,
          title: '',
          type: 'text',
          content: '',
          duration_minutes: 10
        }
      ]
    }));
  };

  const updateLesson = (index, field, value) => {
    setCourseData(prev => {
      const newLessons = [...prev.lessons];
      newLessons[index] = { ...newLessons[index], [field]: value };
      return { ...prev, lessons: newLessons };
    });
  };

  const removeLesson = (index) => {
    if (courseData.lessons.length <= 1) {
      toast.error("Course must have at least one lesson");
      return;
    }
    setCourseData(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, i) => i !== index)
    }));
  };

  // Quiz handlers
  const addQuestion = () => {
    setCourseData(prev => ({
      ...prev,
      quiz_questions: [
        ...prev.quiz_questions,
        {
          id: `q_${Date.now()}`,
          question: '',
          type: 'multiple_choice',
          options: ['', '', '', ''],
          correct_answer: 0
        }
      ]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setCourseData(prev => {
      const newQuestions = [...prev.quiz_questions];
      newQuestions[index] = { ...newQuestions[index], [field]: value };
      return { ...prev, quiz_questions: newQuestions };
    });
  };

  const updateQuestionOption = (qIndex, optIndex, value) => {
    setCourseData(prev => {
      const newQuestions = [...prev.quiz_questions];
      const newOptions = [...newQuestions[qIndex].options];
      newOptions[optIndex] = value;
      newQuestions[qIndex] = { ...newQuestions[qIndex], options: newOptions };
      return { ...prev, quiz_questions: newQuestions };
    });
  };

  const removeQuestion = (index) => {
    if (courseData.quiz_questions.length <= 1) {
      toast.error("Course must have at least one quiz question");
      return;
    }
    setCourseData(prev => ({
      ...prev,
      quiz_questions: prev.quiz_questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!courseData.title.trim()) {
      toast.error("Please enter a course title");
      return;
    }
    if (!courseData.description.trim()) {
      toast.error("Please enter a course description");
      return;
    }
    
    const emptyLessons = courseData.lessons.filter(l => !l.title.trim() || !l.content.trim());
    if (emptyLessons.length > 0) {
      toast.error("All lessons must have a title and content");
      return;
    }
    
    const emptyQuestions = courseData.quiz_questions.filter(q => !q.question.trim());
    if (emptyQuestions.length > 0) {
      toast.error("All quiz questions must have a question text");
      return;
    }

    setSaving(true);
    try {
      // Calculate total duration
      const totalDuration = courseData.lessons.reduce((sum, l) => sum + (l.duration_minutes || 0), 0);
      
      await axios.post(`${API}/courses`, {
        ...courseData,
        duration_minutes: totalDuration,
        lessons: courseData.lessons.map((l, i) => ({
          ...l,
          order: i + 1
        })),
        quiz_questions: courseData.quiz_questions.map((q, i) => ({
          ...q,
          order: i + 1
        }))
      }, { withCredentials: true });
      
      toast.success("Course created successfully!");
      navigate('/courses');
    } catch (error) {
      console.error("Error creating course:", error);
      toast.error(error.response?.data?.detail || "Failed to create course");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="course-builder">
        {/* Header */}
        <div className="mb-8">
          <Link to="/courses" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courses
          </Link>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Create New Course
          </h1>
          <p className="text-muted-foreground mt-1">
            Build a custom training course with lessons and quizzes.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Course Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Course Title</Label>
                <Input
                  id="title"
                  value={courseData.title}
                  onChange={(e) => handleCourseChange('title', e.target.value)}
                  placeholder="e.g., Forklift Safety Fundamentals"
                  data-testid="course-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={courseData.description}
                  onChange={(e) => handleCourseChange('description', e.target.value)}
                  placeholder="Brief description of what employees will learn..."
                  data-testid="course-description-input"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={courseData.category} onValueChange={(v) => handleCourseChange('category', v)}>
                    <SelectTrigger data-testid="course-category-select">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estimated Duration</Label>
                  <p className="text-sm text-muted-foreground mt-2">
                    {courseData.lessons.reduce((sum, l) => sum + (l.duration_minutes || 0), 0)} minutes (calculated from lessons)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lessons */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Lessons</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addLesson}>
                <Plus className="w-4 h-4 mr-1" />
                Add Lesson
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <Accordion type="multiple" className="w-full">
                {courseData.lessons.map((lesson, index) => (
                  <AccordionItem key={lesson.id} value={lesson.id}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        <Badge variant="secondary">Lesson {index + 1}</Badge>
                        <span className="font-medium">{lesson.title || 'Untitled Lesson'}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Lesson Title</Label>
                          <Input
                            value={lesson.title}
                            onChange={(e) => updateLesson(index, 'title', e.target.value)}
                            placeholder="Lesson title..."
                            data-testid={`lesson-title-${index}`}
                          />
                        </div>
                        <div>
                          <Label>Duration (minutes)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={lesson.duration_minutes}
                            onChange={(e) => updateLesson(index, 'duration_minutes', parseInt(e.target.value) || 0)}
                            data-testid={`lesson-duration-${index}`}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Content Type</Label>
                        <Select value={lesson.type} onValueChange={(v) => updateLesson(index, 'type', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {lessonTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Content</Label>
                        <Textarea
                          value={lesson.content}
                          onChange={(e) => updateLesson(index, 'content', e.target.value)}
                          placeholder={lesson.type === 'video' ? 'Enter video URL or embed code...' : 'Enter lesson content...'}
                          className="min-h-[150px]"
                          data-testid={`lesson-content-${index}`}
                        />
                      </div>
                      
                      {courseData.lessons.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLesson(index)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove Lesson
                        </Button>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Quiz Questions */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                Quiz Questions
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-1" />
                Add Question
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {courseData.quiz_questions.map((question, qIndex) => (
                <div key={question.id} className="p-4 border border-slate-200 rounded-lg space-y-4">
                  <div className="flex items-start justify-between">
                    <Badge variant="secondary">Question {qIndex + 1}</Badge>
                    {courseData.quiz_questions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(qIndex)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div>
                    <Label>Question</Label>
                    <Input
                      value={question.question}
                      onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                      placeholder="Enter your question..."
                      data-testid={`question-text-${qIndex}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Answer Options (select correct answer)</Label>
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct_${question.id}`}
                          checked={question.correct_answer === optIndex}
                          onChange={() => updateQuestion(qIndex, 'correct_answer', optIndex)}
                          className="w-4 h-4 text-accent"
                        />
                        <Input
                          value={option}
                          onChange={(e) => updateQuestionOption(qIndex, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                          className={question.correct_answer === optIndex ? 'border-emerald-500' : ''}
                          data-testid={`question-${qIndex}-option-${optIndex}`}
                        />
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      Select the radio button next to the correct answer
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to="/courses">
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} data-testid="create-course-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Create Course
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CourseBuilder;
